import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CircleMarker, MapContainer, Polyline, Popup, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { dms, inr, niceDate } from "./api";
import { PageHeader } from "./OwnerPages";
import { OSM_NOTE, STATUS_LABEL, Scorecard, ShopRow, TARGET_KEYS, TARGET_LABELS, fmtValue, timeOf } from "./DayPlanPages";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { AlertTriangle, Download, MapPin } from "lucide-react";

const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;
const selectCls = "mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white";
const istDay = (offset = 0) => {
  const d = new Date(Date.now() + 330 * 60000 + offset * 86400000);
  return d.toISOString().slice(0, 10);
};

function FitRoute({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length) map.fitBounds(points, { padding: [24, 24], maxZoom: 15 });
  }, [points, map]);
  return null;
}

function RouteMap({ route, stops }) {
  const line = useMemo(() => route.map(p => [p.lat, p.lng]), [route]);
  const all = useMemo(() => [...line, ...stops.map(s => [s.lat, s.lng])], [line, stops]);
  if (all.length === 0) {
    return <div className="h-40 flex items-center justify-center text-sm text-slate-400 border border-slate-200 rounded-lg">No GPS points for this day</div>;
  }
  return (
    <div className="h-72 rounded-lg overflow-hidden border border-slate-200" data-testid="hr-pva-map">
      <MapContainer center={all[0]} zoom={13} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <FitRoute points={all} />
        {line.length > 1 && <Polyline positions={line} color="#2563eb" weight={3} opacity={0.8} />}
        {line.length > 0 && <CircleMarker center={line[0]} radius={6} pathOptions={{ color: "#059669", fillOpacity: 1 }}><Popup>Start</Popup></CircleMarker>}
        {line.length > 1 && <CircleMarker center={line[line.length - 1]} radius={6} pathOptions={{ color: "#e11d48", fillOpacity: 1 }}><Popup>Last point</Popup></CircleMarker>}
        {stops.map(s => (
          <CircleMarker key={s.in_at} center={[s.lat, s.lng]} radius={7} pathOptions={{ color: "#a67c00", fillOpacity: 0.7 }}>
            <Popup><b>{s.name}</b><br />{timeOf(s.in_at)} – {timeOf(s.out_at)} · {s.minutes} min</Popup>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
}

const TIMELINE_DOT = { punch_in: "bg-emerald-500", punch_out: "bg-rose-500", stop: "bg-[#c9a227]", order: "bg-indigo-500" };

function Timeline({ events }) {
  if (!events.length) return <div className="text-sm text-slate-400">No activity recorded</div>;
  return (
    <div className="space-y-2" data-testid="hr-pva-timeline">
      {events.map((e, i) => (
        <div key={`${e.at}-${i}`} className="flex gap-2 text-sm">
          <span className="w-16 shrink-0 text-xs text-slate-500 pt-0.5">{timeOf(e.at)}</span>
          <span className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${TIMELINE_DOT[e.type] || "bg-slate-400"}`} />
          <div className="min-w-0">
            <div className="font-medium text-slate-900 break-words">{e.title}</div>
            {e.detail && <div className="text-xs text-slate-500">{e.detail}{e.out_at ? ` · until ${timeOf(e.out_at)}` : ""}</div>}
            {e.amount != null && <div className="text-xs text-slate-500">{inr(e.amount)}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{children}</div>;
}

function DayDetail({ person, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    dms.hrPlanVsActualDetail(person.salesperson_id, person.date)
      .then(d => { if (alive) setData(d); })
      .catch(e => { toast.error(errMsg(e, "Could not load this day")); onClose(); });
    return () => { alive = false; };
  }, [person, onClose]);
  const r = data?.row;
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="hr-pva-detail">
        <DialogHeader><DialogTitle>{person.name} · {person.date}</DialogTitle></DialogHeader>
        {!data && <div className="py-8 text-center text-sm text-slate-500">Loading…</div>}
        {data && !r && <div className="py-8 text-center text-sm text-slate-500">No data for this day</div>}
        {r && (
          <div className="space-y-4">
            <div className="text-sm text-slate-600">
              {STATUS_LABEL[r.status] || r.status}
              {r.in_at ? ` · ${niceDate(r.in_at)} to ${r.out_at ? niceDate(r.out_at) : "in progress"}` : ""}
            </div>
            {r.has_plan ? <Scorecard targets={r.targets} achieved={r.achieved} pct={r.pct} showKm /> : (
              <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">No plan was filled for this day.</div>
            )}
            {r.plan_edits > 0 && (
              <div className="text-xs text-slate-600">
                Plan edited {r.plan_edits} time(s). Original targets: {TARGET_KEYS.map(k => `${TARGET_LABELS[k]} ${fmtValue(k, r.original_targets?.[k])}`).join(" · ")}
              </div>
            )}
            {r.areas.length > 0 && <div className="text-sm text-slate-700">Planned areas: {r.areas.join(", ")}</div>}
            <div>
              <SectionTitle>Route</SectionTitle>
              <RouteMap route={data.route} stops={data.stops} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <SectionTitle>Planned shops</SectionTitle>
                <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {r.shops.length === 0 && <div className="p-3 text-sm text-slate-400">No shops in the plan</div>}
                  {r.shops.map(s => (
                    <ShopRow key={s.retailer_id} row={{ ...s, reason_label: s.reason ? `${data.reasons[s.reason] || s.reason}${s.reason_text ? `: ${s.reason_text}` : ""}` : null }} />
                  ))}
                </div>
                {r.plan_removed.length > 0 && <div className="text-xs text-rose-700 mt-2">Removed from plan: {r.plan_removed.join(", ")}</div>}
                <div className="mt-3">
                  <SectionTitle>Unplanned stops</SectionTitle>
                  {r.unplanned.length === 0 && <div className="text-sm text-slate-400">None</div>}
                  {r.unplanned.map(s => (
                    <div key={s.in_at} className="text-sm flex gap-2 py-1">
                      <MapPin size={14} className="text-slate-400 shrink-0 mt-0.5" />
                      <span><b>{s.name}</b> <span className="text-xs text-slate-500">{timeOf(s.in_at)} – {timeOf(s.out_at)} · {s.minutes} min</span></span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <SectionTitle>Timeline</SectionTitle>
                <Timeline events={data.timeline} />
              </div>
            </div>
            {r.remark && <div className="text-sm"><b>Remark:</b> {r.remark}</div>}
            {r.report_deferred && <div className="text-xs text-amber-700">Punched out with the "Fill report later" option.</div>}
            <div className="text-[11px] text-slate-400">{OSM_NOTE}</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const pctCell = (r, k) => {
  if (!r.achieved) return "—";
  const p = r.pct?.[k];
  const low = p != null && p < 50 && r.status !== "in_progress";
  return (
    <span>
      {fmtValue(k, r.achieved[k])}
      {p != null && <span className={`block text-[11px] ${low ? "text-rose-700 font-semibold" : p >= 100 ? "text-emerald-700" : "text-slate-500"}`}>{p}% of {fmtValue(k, r.targets?.[k])}</span>}
    </span>
  );
};

function Tile({ label, value, tone, active, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-left rounded-lg border p-3 transition ${active ? "border-[#c9a227] bg-[#faf6e6]" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-xl font-bold ${tone || "text-slate-900"}`}>{value ?? "—"}</div>
    </button>
  );
}

export function HrPlanActualPage() {
  const [filters, setFilters] = useState(() => ({ date_from: istDay(-6), date_to: istDay(0), rm_id: "", tl_id: "", salesperson_id: "", flag: "" }));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyExport, setBusyExport] = useState(false);
  const [open, setOpen] = useState(null);
  const closeDetail = useCallback(() => setOpen(null), []);

  const params = useMemo(() => Object.fromEntries(Object.entries(filters).filter(([, v]) => v)), [filters]);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    dms.hrPlanVsActual(params)
      .then(d => { if (alive) setData(d); })
      .catch(e => { if (alive) toast.error(errMsg(e, "Could not load the report")); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [params]);

  const setF = (k) => (e) => {
    const v = e.target.value;
    setFilters(f => ({ ...f, [k]: v, ...(k === "rm_id" ? { tl_id: "" } : {}) }));
  };
  const toggleFlag = (flag) => setFilters(f => ({ ...f, flag: f.flag === flag ? "" : flag }));
  const doExport = async () => {
    setBusyExport(true);
    try { await dms.hrPlanVsActualExport(params); }
    catch (e) { toast.error(errMsg(e, "Export failed")); }
    finally { setBusyExport(false); }
  };

  const rows = data?.data || [];
  const teams = (data?.team_leaders || []).filter(t => !filters.rm_id || t.rm_id === filters.rm_id);
  const s = data?.summary;
  return (
    <div>
      <PageHeader title="Plan vs Actual" subtitle="Each salesperson's morning plan compared with what actually happened"
        action={<Button variant="outline" onClick={doExport} disabled={busyExport} data-testid="hr-pva-export"><Download size={14} className="mr-2" /> {busyExport ? "Preparing…" : "Export Excel"}</Button>} />
      <Card className="p-3 mb-3 grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div><Label>From</Label><Input type="date" className="mt-1 min-w-0" value={filters.date_from} onChange={setF("date_from")} data-testid="hr-pva-from" /></div>
        <div><Label>To</Label><Input type="date" className="mt-1 min-w-0" value={filters.date_to} onChange={setF("date_to")} data-testid="hr-pva-to" /></div>
        <div><Label>Regional Manager</Label>
          <select className={selectCls} value={filters.rm_id} onChange={setF("rm_id")} data-testid="hr-pva-rm">
            <option value="">All</option>
            {(data?.rms || []).map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
        <div><Label>Team Leader</Label>
          <select className={selectCls} value={filters.tl_id} onChange={setF("tl_id")} data-testid="hr-pva-tl">
            <option value="">All</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className="col-span-2 lg:col-span-1"><Label>Salesperson</Label>
          <select className={selectCls} value={filters.salesperson_id} onChange={setF("salesperson_id")} data-testid="hr-pva-sp">
            <option value="">All</option>
            {(data?.salespersons || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </Card>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <Tile label="Salesperson days" value={s?.days} active={!filters.flag} onClick={() => setFilters(f => ({ ...f, flag: "" }))} testid="hr-pva-all" />
        <Tile label="Incomplete reports" value={s?.incomplete} tone="text-amber-700" active={filters.flag === "incomplete"} onClick={() => toggleFlag("incomplete")} testid="hr-pva-incomplete" />
        <Tile label="Below 50% of target" value={s?.low_achievement} tone="text-rose-700" active={filters.flag === "low_achievement"} onClick={() => toggleFlag("low_achievement")} testid="hr-pva-low" />
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">No plan filled</div>
          <div className="text-xl font-bold text-slate-900">{s?.no_plan ?? "—"}</div>
        </div>
      </div>
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead><TableHead>Salesperson</TableHead><TableHead>Status</TableHead>
            {TARGET_KEYS.map(k => <TableHead key={k}>{TARGET_LABELS[k]}</TableHead>)}
            <TableHead>Shops</TableHead><TableHead>KM</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={9} className="text-center py-6 text-slate-400">Loading…</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-6 text-slate-400">No data for this filter</TableCell></TableRow>}
            {!loading && rows.map(r => {
              const key = `${r.salesperson_id}-${r.date}`;
              const visited = r.shops.filter(x => x.status === "visited").length;
              const tone = r.low_achievement ? "bg-rose-50 hover:bg-rose-100" : r.incomplete ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-slate-50";
              return (
                <TableRow key={key} className={`cursor-pointer ${tone}`} onClick={() => setOpen(r)} data-testid={`hr-pva-row-${key}`}>
                  <TableCell className="whitespace-nowrap">{r.date}</TableCell>
                  <TableCell className="font-medium whitespace-nowrap">{r.name}</TableCell>
                  <TableCell className="text-xs">
                    {(r.incomplete || r.low_achievement) && <AlertTriangle size={12} className="inline mr-1 text-rose-600" />}
                    {STATUS_LABEL[r.status] || r.status}
                  </TableCell>
                  {TARGET_KEYS.map(k => <TableCell key={k} className="text-xs">{pctCell(r, k)}</TableCell>)}
                  <TableCell className="text-xs whitespace-nowrap">{r.shops.length ? `${visited} / ${r.shops.length}` : "—"}</TableCell>
                  <TableCell className="text-xs">{r.achieved?.km ?? "—"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
      {open && <DayDetail person={open} onClose={closeDetail} />}
    </div>
  );
}
